import { Paper } from '@mui/material';
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import DataTableStyleEnum from '../../Enums/DataTableStyleEnum';
import CookieHelper from '../../Helpers/cookieHelper';
import DataTableHeader from './DataTableHeader';
import DataTableBody from './DataTableBody';
import ItemDetailBox from './ItemDetailBox';
import RoleService from '../../Services/roleService';
import columnResizeHelper from '../../Helpers/columnResizeHelper';
import ErrorMessage from '../ErrorMessage';

// Width of the clickable corner of a cut cell — matches the .cw-cell eye drawn in bravo-app.css.
const CLIPPED_EYE_HIT_WIDTH = 22;

// The dragged column widths ride into the table as CSS custom properties on the <table>
// element, which every cell in the column reads (see columnResizeHelper), so one property
// write resizes a whole column without re-rendering a row.
function getFields(props) {
  return props.type != null && typeof props.type.FieldsToShowInDataTable === 'function'
    ? props.type.FieldsToShowInDataTable()
    : props.FieldsToShowInDataTable;
}

function getBlocks(type) {
  if (type == null) return null;
  const shownBlocks = {};
  // At most one block of an exclusive group is shown at a time. The cookies predate the
  // groups, so a returning user can arrive with both halves of a pair stored as shown; the
  // first declared one wins. Nothing is written back, so relaxing a group later hands them
  // their own choice again.
  const takenGroups = {};
  if (type.BlocksInDataTable) {
    type.BlocksInDataTable.forEach((item) => {
      let value = CookieHelper.getCookie(type.uniquePageIdForBlockCookies + item.name, item.checkedDefault) === 'true';
      if (value && item.exclusiveGroup) {
        if (takenGroups[item.exclusiveGroup]) value = false;
        else takenGroups[item.exclusiveGroup] = true;
      }
      shownBlocks[item.name] = {
        value: value,
        label: item.label,
        bgcolor: item.bgcolor,
        exclusiveGroup: item.exclusiveGroup,
      };
    });
  }
  return shownBlocks;
}

export default function DataTable(props) {
  const [selected, setSelected] = useState(props.selected);
  const [openOnTabName, setOpenOnTabName] = useState(null);
  const [shownBlocks, setShownBlocks] = useState(() => getBlocks(props.type));

  // Mirrors the old componentWillReceiveProps: resync selected when the prop changes.
  useEffect(() => {
    setSelected(props.selected);
  }, [props.selected]);

  // A page's request to open an item's popup on one of its tabs (TabOfDataTable.openDetails). Each request is a new
  // object, so the same item opens again after its popup was closed. A list reloads by mounting a new table, so the
  // request there at mount counts as handled, or every reload would open the popup again.
  const handledDetailRequest = useRef(props.detailRequest);
  useEffect(() => {
    if (props.detailRequest && props.detailRequest !== handledDetailRequest.current) {
      handledDetailRequest.current = props.detailRequest;
      setSelected(props.detailRequest.id);
      setOpenOnTabName(props.detailRequest.tabName);
    }
  }, [props.detailRequest]);

  const colorDictionary = {};
  const tableRef = useRef(null);
  const gridClassName = `bravo-ds FigmaDataTableBorder${props.resizableColumns ? ' col-resizable' : ''}`;

  // Re-apply the stored widths after every render, before paint. The grip's own writes go
  // straight to the DOM and never come back through React, so this is what a fresh mount (the
  // grid remounts after each fetch) and the Columns flyout's Reset both rely on: a column with
  // no stored width has its property cleared here and falls back to its natural width.
  useLayoutEffect(() => {
    if (!props.resizableColumns) return;
    columnResizeHelper.applyColumnWidths(tableRef.current, getFields(props), props.columnWidths);
    columnResizeHelper.markClippedColumns(tableRef.current, getFields(props), props.columnWidths);
  });

  // The eye on a cut cell. It is drawn by CSS rather than rendered per row — a grid this wide
  // cannot afford an extra element in every cell — so the click is caught here and matched by
  // position: anywhere in the eye's corner of a cell that is currently cut. The cell's title is
  // the whole value (DataTableBody.getFullValue), which is what the popup shows.
  const clickOnGrid = (event) => {
    if (!props.resizableColumns) return;
    const cell = event.target.closest ? event.target.closest('.cw-cell.is-clipped') : null;
    if (!cell) return;
    const bounds = cell.getBoundingClientRect();
    if (event.clientX < bounds.right - CLIPPED_EYE_HIT_WIDTH) return;
    ErrorMessage.SetInfo(cell.getAttribute('title'));
  };
  const complex = props.style !== DataTableStyleEnum.Simple;
  const nameOfTheIdField =
    props.type != null && props.type.NameOfTheIdField !== undefined ? props.type.NameOfTheIdField() : '';

  const clickOnCancel = () => {
    setSelected(-1);
    setOpenOnTabName(null);
    if (props.afterClickOnCancel) {
      props.afterClickOnCancel();
    }
  };

  // A popup opened by a click starts on its first tab.
  const ClickOnDataFieldWithDetails = (idValue) => {
    setOpenOnTabName(null);
    setSelected((prev) => (idValue === prev ? -1 : idValue));
  };

  const renderTableRow = (listItem, lineIndex) => (
    <tbody key={`tbody${lineIndex}`}>
      <DataTableBody
        type={props.type}
        listItem={listItem}
        shownBlocks={shownBlocks}
        selected={selected}
        nameOfTheIdField={nameOfTheIdField}
        complex={complex}
        colorDictionary={colorDictionary}
        ClickOnDataFieldWithDetails={ClickOnDataFieldWithDetails}
        FieldsToShowInDataTable={props.FieldsToShowInDataTable}
        renderInputFieldFunction={props.renderInputFieldFunction}
        Lineindex={lineIndex}
        DataTableType={props.DataTableType}
        ModalFeature={props.ModalFeature}
        hiddenColumns={props.hiddenColumns}
        resizableColumns={props.resizableColumns}
      />
    </tbody>
  );

  const renderDetailBox = (listItem, key) => {
    if (props.rolekey && !RoleService.CheckUserPermission('detailBox', props.rolekey, 'visit')) return null;
    return (
      <ItemDetailBox
        key={key}
        selected={selected}
        item={listItem}
        type={props.type}
        getItemTypeFunction={props.getItemTypeFunction}
        clickOnCancel={clickOnCancel}
        popupEnabled={props.popupEnabled}
        nameOfTheIdField={nameOfTheIdField}
        openOnTabName={openOnTabName}
      />
    );
  };

  const renderBlockButtons = () => {
    if (!complex || shownBlocks === null) return undefined;
    const blockKeys = Object.keys(shownBlocks);
    if (blockKeys.length < 1) return null;
    return (
      <div className='d-flex '>
        {blockKeys.map((k) => (
          <div
            className='btn btn-light border btn-sm m-1'
            key={`block${k}`}
          >
            <input
              type='checkbox'
              id={`blockCheckBox${k}`}
              checked={shownBlocks[k].value}
              onChange={() => {
                const tempShownBlocks = { ...shownBlocks };
                const turningOn = !tempShownBlocks[k].value;
                tempShownBlocks[k] = { ...tempShownBlocks[k], value: turningOn };
                CookieHelper.setCookie(props.type.uniquePageIdForBlockCookies + k, turningOn);
                // Switching one on puts the rest of its group away, so a pair that reports the
                // same thing at two levels of detail never sits side by side repeating its
                // column names over figures that disagree. Switching the shown one off again
                // leaves the group empty, which is a fine place to be.
                if (turningOn && tempShownBlocks[k].exclusiveGroup) {
                  blockKeys.forEach((other) => {
                    if (other === k || !tempShownBlocks[other].value) return;
                    if (tempShownBlocks[other].exclusiveGroup !== tempShownBlocks[k].exclusiveGroup) return;
                    tempShownBlocks[other] = { ...tempShownBlocks[other], value: false };
                    CookieHelper.setCookie(props.type.uniquePageIdForBlockCookies + other, false);
                  });
                }
                setShownBlocks(tempShownBlocks);
              }}
            />{' '}
            <label
              htmlFor={`blockCheckBox${k}`}
              className='m-1'
            >
              {` ${shownBlocks[k].label}`}
            </label>
          </div>
        ))}
      </div>
    );
  };

  const renderSmallDataTable = () => {
    if (props.thereIsNoSuchRowMessageCheck && props.itemList.length === 0) {
      return (
        <div className='bravo-ds'>
          <div className='empty-state'>
            <div className='ei'>
              <i className='fa-solid fa-table-list' />
            </div>
            <b>Nothing to show</b>
            <span>{props.thereIsNoSuchRowMessage}</span>
          </div>
        </div>
      );
    }
    return (
      <div className={gridClassName}>
        {renderBlockButtons()}
        <div className='table-wrap'>
          <table
            className='fixed_header stripped tab grid'
            ref={tableRef}
            onClick={clickOnGrid}
          >
            <DataTableHeader
              changeOrderBy={props.changeOrderBy}
              FieldsToShowInDataTable={props.FieldsToShowInDataTable}
              itemList={props.headeritemList}
              orderBy={props.orderBy}
              orderDirection={props.orderDirection}
              type={props.type}
              DataTableType={props.DataTableType}
              ModalFeature={props.ModalFeature}
              shownBlocks={shownBlocks}
              hiddenColumns={props.hiddenColumns}
              resizableColumns={props.resizableColumns}
              onResizeColumn={props.onResizeColumn}
            />
            {Array.isArray(props.itemList)
              ? props.itemList.map((item, Lineindex) => renderTableRow(item, Lineindex))
              : renderTableRow(props.itemList, '')}
          </table>
        </div>
        {Array.isArray(props.itemList) ? props.itemList.map(renderDetailBox) : ''}
      </div>
    );
  };

  if (props.smallDataTable) {
    return renderSmallDataTable();
  }

  if (props.itemList && props.thereIsNoSuchRowMessageCheck) {
    if (props.itemList.length === 0) {
      return (
        <div className='bravo-ds'>
          <div className='empty-state'>
            <div className='ei'>
              <i className='fa-solid fa-table-list' />
            </div>
            <b>Nothing to show</b>
            <span>{props.thereIsNoSuchRowMessage}</span>
          </div>
        </div>
      );
    }
    return (
      <div className={gridClassName}>
        <Paper elevation={0}>
          {renderBlockButtons()}
          <div className='table-wrap'>
            <table
              className='fixed_header stripped tab grid'
              ref={tableRef}
              onClick={clickOnGrid}
            >
              <DataTableHeader
                changeOrderBy={props.changeOrderBy}
                FieldsToShowInDataTable={props.FieldsToShowInDataTable}
                itemList={props.headeritemList}
                orderBy={props.orderBy}
                orderDirection={props.orderDirection}
                type={props.type}
                DataTableType={props.DataTableType}
                ModalFeature={props.ModalFeature}
                shownBlocks={shownBlocks}
                hiddenColumns={props.hiddenColumns}
                resizableColumns={props.resizableColumns}
                onResizeColumn={props.onResizeColumn}
              />
              {Array.isArray(props.itemList)
                ? props.itemList.map((item, Lineindex) => renderTableRow(item, Lineindex))
                : renderTableRow(props.itemList, '')}
            </table>
          </div>
          {props.itemList.map((item, key) => renderDetailBox(item, key))}
        </Paper>
      </div>
    );
  }

  return 'Error! ErrorCode: DTF208';
}

DataTable.defaultProps = {
  itemList: [],
  selectable: true,
  style: DataTableStyleEnum.ComplexWithoutTranslation,
  thereIsNoSuchRowMessage: 'There is no such row.',
  popupEnabled: false,
  thereIsNoSuchRowMessageCheck: true,
  rolekey: '',
  smallDataTable: false,
  afterClickOnCancel: () => {},
};
